import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import { useCallback, useEffect, useMemo, useState } from 'react';
import get from 'lodash.get';
import deepEqual from 'fast-deep-equal';
import {
  specificProjectFragmentLegacy,
  specificResourceRequestWithAllocationsFragment,
  specificResourceRequestWithAllocationsScheduleFragment,
  resourceMatchesFragment
} from '~/modules/resourcing/common/fragments';
import { HiringStatus, ResourceRequestAccessLevel } from '~/types';
import { useSessionStorage, useHasFeatureFlag } from '~/modules/common/hooks';
import { mapScaleToPeriodResolution } from '~/modules/resourcing/common/util';
import { RESOURCE_ALLOCATION_CHART_SUB_TAB_ENUM } from './useResourceAllocationChartTabStateContext';

export const RESOURCE_REQUESTS_ALLOCATIONS_QUERY = gql`
  query resourceRequests(
    $cursor: String
    $roleUri: String
    $projectUri: String
    $requestStatusList: [ResourceRequestStatus]
    $requestAccessLevel: ResourceRequestAccessLevel
    $filter: ResourceRequestsFilter
    $limit: Int = 500
    $minimumLimit: Int
  ) {
    resourceRequests(
      limit: $limit
      cursor: $cursor
      roleUri: $roleUri
      projectUri: $projectUri
      requestStatusList: $requestStatusList
      requestAccessLevel: $requestAccessLevel
      filter: $filter
      minimumLimit: $minimumLimit
    ) {
      resourceRequests {
        project {
          ...SpecificProjectFragmentLegacy
        }
        ...SpecificResourceRequestWithAllocation
      }
      nextPageCursor
    }
  }
  ${specificResourceRequestWithAllocationsFragment}
  ${specificProjectFragmentLegacy}
`;

export const RESOURCE_REQUESTS_ALLOCATIONS_QUERY_WITH_SCHEDULE = gql`
  query resourceRequests(
    $cursor: String
    $roleUri: String
    $projectUri: String
    $requestStatusList: [ResourceRequestStatus]
    $requestAccessLevel: ResourceRequestAccessLevel
    $filter: ResourceRequestsFilter
    $limit: Int = 500
    $minimumLimit: Int
    $chartDateRange: DateRangeInput
    $periodResolution: PeriodResolutionOption!
    $resourceMatchesPage: Int = 1
    $resourceMatchesPageSize: Int = 1
    $isPsaPraaResourceMatchingEnabled: Boolean = false
  ) {
    resourceRequests(
      limit: $limit
      cursor: $cursor
      roleUri: $roleUri
      projectUri: $projectUri
      requestStatusList: $requestStatusList
      requestAccessLevel: $requestAccessLevel
      filter: $filter
      minimumLimit: $minimumLimit
    ) {
      resourceRequests {
        mergedResourceRequests {
          id
        }
        project {
          ...SpecificProjectFragmentLegacy
        }
        ...SpecificResourceRequestWithAllocationSchedule
        ...ResourceMatchesFragment
      }
      nextPageCursor
    }
  }
  ${specificResourceRequestWithAllocationsScheduleFragment}
  ${specificProjectFragmentLegacy}
  ${resourceMatchesFragment}
`;

const useResourceRequests = ({
  roleUri,
  projectUri,
  requestStatusList,
  requestAccessLevel = ResourceRequestAccessLevel.All,
  filter,
  chartDateRange,
  scale,
  resourcePlanSubstatusFilter,
  isHiringStatusSubFilterEnabled
}) => {
  const isPsaPraaResourceMatchingEnabled = useHasFeatureFlag({
    featureFlag: 'isPsaPraaResourceMatchingEnabled'
  });

  const variables = useMemo(
    () => ({
      cursor: null,
      roleUri,
      projectUri,
      requestStatusList,
      requestAccessLevel,
      filter: {
        ...filter,
        ...(isHiringStatusSubFilterEnabled && {
          hiringStatuses:
            resourcePlanSubstatusFilter ===
            RESOURCE_ALLOCATION_CHART_SUB_TAB_ENUM.HIRINGREQUESTED
              ? [HiringStatus.HiringRequested]
              : [],
          excludedHiringStatuses:
            resourcePlanSubstatusFilter ===
            RESOURCE_ALLOCATION_CHART_SUB_TAB_ENUM.TOBEHIRED
              ? [HiringStatus.HiringRequested, HiringStatus.HiringWithdrawn]
              : []
        })
      },
      limit: 50,
      minimumLimit: 10,
      chartDateRange,
      periodResolution: mapScaleToPeriodResolution(scale),
      isPsaPraaResourceMatchingEnabled
    }),
    [
      roleUri,
      projectUri,
      requestStatusList,
      requestAccessLevel,
      filter,
      isHiringStatusSubFilterEnabled,
      resourcePlanSubstatusFilter,
      chartDateRange,
      scale,
      isPsaPraaResourceMatchingEnabled
    ]
  );

  const { setValue, storedValue } = useSessionStorage(
    'RESOURCE-REQUESTS-QUERY-VARIABLES',
    null
  );

  useEffect(() => {
    if (!deepEqual(storedValue, variables)) {
      setValue(variables);
    }
  }, [setValue, variables, storedValue]);

  const [loadingMore, setLoadingMore] = useState(false);
  const { loading, data, fetchMore } = useQuery(
    RESOURCE_REQUESTS_ALLOCATIONS_QUERY_WITH_SCHEDULE,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables,
      errorPolicy: 'all'
    }
  );

  const resourceRequests = get(data, 'resourceRequests.resourceRequests') || [];
  const nextPageCursor = get(data, 'resourceRequests.nextPageCursor');
  const hasMoreRows = Boolean(nextPageCursor);

  const loadMoreRows = useCallback(async () => {
    if (loading || loadingMore || !hasMoreRows) return;

    setLoadingMore(true);
    try {
      await fetchMore({
        query: RESOURCE_REQUESTS_ALLOCATIONS_QUERY_WITH_SCHEDULE,
        variables: {
          ...variables,
          cursor: nextPageCursor
        }
      });
    } finally {
      setLoadingMore(false);
    }
  }, [loading, loadingMore, hasMoreRows, fetchMore, variables, nextPageCursor]);

  return {
    loadingRows: loading,
    resourceRequests: resourceRequests.filter(r =>
      requestStatusList.includes(r.requestStatus)
    ),
    loadMoreRows,
    hasMoreRows,
    loadingMoreRows: loadingMore
  };
};

export default useResourceRequests;
